
import { Type } from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { XYChart, ColumnSeries, ValueAxis, CategoryAxis, AxisLabel, LabelBullet, Column } from '@amcharts/amcharts4/charts';

import { AbstractChartOptionsModel } from './abstract-chart-options.model';
import { CategoryValueXYChartOptions, StackTypesEnum } from './barChart/category-value-x-y-chart-options.model';
import { chartColors } from '../enums/chart-colors.enum';
import { ChartMetadataModel } from './chart-metadata.model';
import { ChartMetricsEnum } from '../../Reports/enums/chart-metrics.enum';
import { ChartModel } from './chart.model';
import { ChartSeries } from './chart-series.model';
import { ChartSizeHelper } from '../helpers/chart-size.helper';
import { ChartSpecificTypesEnum } from '../../Reports/enums/chart-specific-types.enum';
import {
    chartSpecificTypesToBaseAxisDirectionsMap,
    chartSpecificTypesToValueAxisDirectionsMap,
    ReportBlockTypeEnum
} from '../../Reports/enums/report-block-type.enum';
import { currencySymbolMap } from '../../../Core/Users/enums/user-currency';
import { CustomLegendItem } from './custom-legend-item.model';
import { DataPointModel } from './data-point.model';
import { LabelDisplayOptionsEnum } from '../../Misc/enums/label-display-options.enum';
import { SeriesModel } from './series.model';
import { ColorModel } from '../../../Common/Forms/models/color.model';
import { reportsBlockMappingConfigurationMap } from '../../Reports/models/reports-block-mapping-configuration.model';


export class CategoryValueXYChart extends ChartModel {

    declare amchart: XYChart;
    declare options: CategoryValueXYChartOptions;

    categoryAxis: CategoryAxis;
    responsiveCategoryAxis: CategoryAxis;
    categoryLabel: AxisLabel;
    valueAxis: ValueAxis;
    valueLabel: LabelBullet;
    valueLabels: { labelBullet: LabelBullet, chartSeries: ChartSeries }[] = [];
    totalSeries: ColumnSeries;
    chartBlockContainer: HTMLElement;
    smallChartDisplayLimit: number = 3;
    chartDisplayLimit: number = 12;
    readonly chartType: Type<XYChart> = XYChart;
    readonly minColumnWidthForRotation = 100;
    private isExporting = false;
    private _blockContainerObserver: ResizeObserver;

    constructor(
        series: SeriesModel[],
        options: AbstractChartOptionsModel = {},
        metadata?: ChartMetadataModel,
        chartSeries?: ChartSeries[],
        // todo: clickcallback, used in social, the chart is not using the model yet
    ) {
        super(series, options, metadata);
        this.chartSeries = chartSeries;
    }

    public initChart(element: HTMLDivElement): void {
        super.initChart(element, this.chartType);

        if (this.options.resourceLinkOnNewLine) {
            if (this.metadataContainer) {
                this.metadataContainer.layout = 'vertical';
            }
            if (this.dateRangeLabel) {
                this.dateRangeLabel.width = am4core.percent(100);
            }
            if (this.primarySeriesLabel) {
                this.primarySeriesLabel.width = am4core.percent(100);
                this.primarySeriesLabel.textAlign = 'start';
            }
        }
        if (this.options.includeScrollbarX && this.isBarChart() && !this.options.responsive) {
            this.amchart.scrollbarX = new am4core.Scrollbar();
            this.amchart.scrollbarX.dx = 15;
            this.amchart.scrollbarX.dy = -12;
            this.amchart.scrollbarX.scale = 0.75;
            this.amchart.zoomOutButton.disabled = false;
        }
        this.amchart.numberFormatter.bigNumberPrefixes = [
            { 'number': 1e+3, 'suffix': 'K' },
            { 'number': 1e+6, 'suffix': 'M' },
            { 'number': 1e+9, 'suffix': 'B' },
            { 'number': 1e+12, 'suffix': 'T' },
            { 'number': 1e+15, 'suffix': 'Q' }
        ];
        this.amchart.numberFormatter.smallNumberPrefixes = [];

        this.setUpCategoryAxis();
        this.setUpValueAxis();
        this.setUpCursor();
        this.setUpLegend();
        this.findChartBlockContainer();
    }

    setUpCategoryAxis(): void {
        const categoryAxisDirection = this.amchart[chartSpecificTypesToBaseAxisDirectionsMap.get(this.options.chartSpecificType)];
        this.categoryAxis = categoryAxisDirection.push(new CategoryAxis());
        this.categoryAxis.dataFields.category = 'label';
        this.categoryAxis.renderer.minGridDistance = 1;
        this.categoryAxis.renderer.grid.template.disabled = true;
        this.categoryLabel = this.categoryAxis.renderer.labels.template;
        if (this.options.hideCategoryLabel) {
            if (this.header && this.isBarChart()) {
                this.header.marginBottom = -5;
            }
            this.categoryLabel.disabled = true;
        }
        this.categoryLabel.fontSize = '0.75rem';
        if (this.isBarChart()) {
            this.amchart.paddingBottom = 15;
            if (this.header) {
                this.header.padding(2, 0, 0, 0);
            }
            // CATEGORY LABEL APPEARANCE
            this.categoryLabel.paddingLeft = 0;
            this.categoryLabel.paddingBottom = 20;
            this.categoryLabel.location = 1;
            if (this.options.categoryLabelStackedOnTop) {
                this.categoryAxis.renderer.inside = true;
                if (this.options.styles.categoryLabelVerticalShiftPx) {
                    this.categoryLabel.dy = this.options.styles.categoryLabelVerticalShiftPx;
                } else {
                    this.categoryLabel.adapter.add('dy', (distance, target) => {
                        const cellHeight = (target.axis?.contentHeight || 1) / (target.axis?.dataItems.length || 1);
                        return Math.max(cellHeight / 15, 5);
                    });
                }
            }
        } else {
            this.amchart.paddingBottom = 5;
            this.categoryLabel.horizontalCenter = 'middle';
            this.categoryLabel.verticalCenter = 'middle';
            this.categoryLabel.textAlign = 'middle';
            this.categoryLabel.truncate = true;
            this.categoryLabel.rotation = -90;
            this.categoryLabel.location = 0.5;
            this.categoryLabel.padding(10, 0, 10, 0);
            this.categoryLabel.fullWords = false;
        }

        this.categoryAxis.renderer.cellStartLocation = 0.2;
        this.categoryAxis.renderer.cellEndLocation = 0.8;
    }

    updateCategoryLabelRotation(categoryLabel: AxisLabel): void {
        if (
            this.options.chartSpecificType === ChartSpecificTypesEnum.COLUMN ||
            this.options.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_COLUMN
        ) {
            const columnWidth = this.categoryAxis.pixelWidth / (this.amchart.data?.length || 1) || this.minColumnWidthForRotation;
            if (columnWidth >= this.minColumnWidthForRotation) {
                categoryLabel.rotation = 0;
                categoryLabel.dx = 0;
                categoryLabel.horizontalCenter = 'middle';
                categoryLabel.verticalCenter = 'top';
                this.categoryLabel.maxWidth = columnWidth * 0.9;
            } else {
                categoryLabel.rotation = -(Math.acos(columnWidth / this.minColumnWidthForRotation) / Math.PI * 180 || 90);
                categoryLabel.dx = -15;
                categoryLabel.horizontalCenter = 'right';
                categoryLabel.verticalCenter = 'top';
                this.categoryLabel.maxWidth = this.minColumnWidthForRotation;
                // adding left/right space for columns & its labels
                this.amchart.chartContainer.paddingLeft = 40;
                this.amchart.chartContainer.paddingRight = 40;
            }
        }
    }

    setUpValueAxis(): void {
        const valueAxisDirection = this.amchart[chartSpecificTypesToValueAxisDirectionsMap.get(this.options.chartSpecificType)];
        const valueAxis = valueAxisDirection.push(new ValueAxis());
        this.valueAxis = valueAxis;
        if (this.isBarChart()) {
            valueAxis.renderer.baseGrid.disabled = true;
            valueAxis.renderer.grid.template.disabled = true;
            valueAxis.renderer.labels.template.disabled = true;
            this.calculateBarSeriesLabelWidth();
        } else {
            valueAxis.renderer.baseGrid.disabled = false;
            valueAxis.renderer.grid.template.disabled = this.options.stacked;
            valueAxis.renderer.labels.template.disabled = false;
            valueAxis.renderer.labels.template.paddingLeft = 0;
            valueAxis.numberFormatter.numberFormat = '#.#a';
            if (this.options.hideCategoryLabel) {
                this.amchart.paddingBottom = 20;
            }
        }

        // Ideally we should provide the percent value so amchart does not need to do auto calculation
        // however we do not calculate well when the user toggles certain legends, so we need to rely on amchart calculation
        if (this.options.stacked) {
            if (this.options.stackType === StackTypesEnum.percent) {
                valueAxis.renderer.labels.template.disabled = true;
                valueAxis.max = 100;
                valueAxis.calculateTotals = true;
                valueAxis.renderer.labels.template.adapter.add('text', (text, target) => {
                    if (text && !text.endsWith('%')) {
                        return text + '%';
                    }
                    return text;
                });
            }
        }
        valueAxis.min = 0;
        valueAxis.strictMinMax = true;

        if (this.options.showGrid) {
            this.valueAxis.renderer.line.disabled = false;
            this.valueAxis.renderer.line.strokeOpacity = 1;
            this.valueAxis.renderer.line.strokeWidth = 1;
            this.valueAxis.renderer.line.stroke = am4core.color('#ced4da');
            this.valueAxis.renderer.grid.template.disabled = false;
            this.valueAxis.renderer.labels.template.disabled = false;
        } else {
            this.valueAxis.renderer.line.disabled = true;
            this.valueAxis.renderer.line.strokeOpacity = 1;
            this.valueAxis.renderer.line.strokeWidth = 2;
            this.valueAxis.renderer.line.stroke = am4core.color('#ced4da');
            this.valueAxis.title.text = null;
        }
        if (this.options.valueAxisTitle) {
            this.valueAxis.title.text = this.options.valueAxisTitle;
        }
    }

    setUpCursor() {
        this.amchart.cursor = new am4charts.XYCursor();
        this.amchart.cursor.maxTooltipDistance = 0;
        this.amchart.cursor.lineY.disabled = true;
        this.amchart.cursor.lineX.disabled = true;
        this.amchart.cursor.lineX.strokeWidth = 0;
        this.amchart.cursor.lineX.strokeOpacity = 0;
        this.valueAxis.cursorTooltipEnabled = false;
        this.categoryAxis.cursorTooltipEnabled = false;
    }


    calculateBarSeriesLabelWidth(showLabel: boolean = true): void {
        if (!showLabel) {
            this.valueAxis.paddingRight = 0;
            return;
        }
        let rightPadding = 0;
        if (!this.options.stacked && !this.options.includeTotalSeries) {
            if (this.metadata?.labelDisplayOptions) {
                const doCount: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.count);
                const doPercent: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.percent) && !this.options.noPercentage;
                if (doCount) {
                    rightPadding += 55;
                }
                if (doPercent) {
                    rightPadding += 45;
                }
            }
            if (this.options.trendIndicators) {
                rightPadding = 75;
            }
        }
        this.valueAxis.paddingRight = rightPadding;
    }

    addSeries(
        seriesIndex: number,
        chartSeries: ChartSeries
    ): void {
        const series = this.amchart.series.push(new ColumnSeries());

        switch (this.options.chartSpecificType) {
            case ChartSpecificTypesEnum.BAR:
            case ChartSpecificTypesEnum.MULTI_STACKED_BAR:
                this.setUpBarSeries(series, seriesIndex, chartSeries);
                break;
            case ChartSpecificTypesEnum.COLUMN:
            case ChartSpecificTypesEnum.MULTI_STACKED_COLUMN:
                this.setUpColumnSeries(series, seriesIndex, chartSeries);
                break;
        }
        series.name = chartSeries.label;
        series.dataFields['percent'] = 'percent' + seriesIndex;
        series.numberFormatter.smallNumberThreshold = 0;
        if (this.options.stacked) {
            series.stacked = true;
        }

        const columnTemplate = series.columns.template;
        columnTemplate.strokeWidth = 1;
        if (this.options.dataPointDistinctColor) {
            series.columns.template.adapter.add('fill', (fill, target) => {
                let color = am4core.color(this.styles.colorMap[target.dataItem.dataContext['key']] || this.styles.colorMap['barColor'] || chartColors.agprBlue);
                if (this.options.useReportSeriesColor) {
                    let columnIndex = this.amchart.data.findIndex(x => x.key === target.dataItem.dataContext['key']);
                    if (columnIndex > -1) {
                        if (this.options.reverseBarSeriesOrder || this.options.reverseSourceOrder) {
                            columnIndex = this.amchart.data.length - 1 - columnIndex;
                        }
                        const seriesColor = this.options.seriesColorMapKey
                            ? this.styles.colorMap[this.options.seriesColorMapKey + (columnIndex + 1)]
                            : this.styles.colorMap['seriesColor' + columnIndex];
                        if (seriesColor) {
                            color = am4core.color(seriesColor);
                        }
                    }
                }
                target.stroke = color;
                return color;
            });
        } else {
            let color = am4core.color(this.styles.colorMap['barColor'] || this.styles.colorMap['seriesColor0'] || chartColors.agprBlue);

            if (chartSeries?.key) {
                color = am4core.color(this.styles.colorMap[chartSeries.key] || color);
            }
            if (chartSeries?.colorMapKey) {
                color = am4core.color(this.styles.colorMap[chartSeries.colorMapKey] || color) ;
            }
            columnTemplate.stroke = color;
            columnTemplate.fill = color;
        }

        // this line is actually necessary, which changed with some commit between amcharts version 4.6.1 and 4.10.18
        // otherwise, no tooltips will be shown on hover because the chart assumes no tooltip is desired, though we
        // set an adapter for it in ReportsChartComponent
        // this can also be solved by adding a cursor to the chart, but then you'd have to add more code
        // to remove all of the styling of the cursor.
        columnTemplate.tooltipHTML = '';

        // set tooltip style
        series.tooltip = new am4core.Tooltip();
        series.tooltip.getFillFromObject = false;
        series.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        series.tooltip.background.stroke = am4core.color('#ced4da');
        series.tooltip.background.cornerRadius = 8;
        series.tooltip.background.fillOpacity = 1;
        // clear tooltip background shadow
        series.tooltip.background.filters.clear();
        series.tooltip.label.padding(0, 0, 0, 0);
        series.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        series.tooltip.keepTargetHover = true;
        series.tooltip.label.interactionsEnabled = true;
        if (this.options.chartSpecificType === ChartSpecificTypesEnum.BAR ||
            this.options.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR) {
            series.tooltip.pointerOrientation = 'horizontal';
        } else if (this.options.chartSpecificType === ChartSpecificTypesEnum.COLUMN ||
            this.options.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_COLUMN) {
            series.tooltip.pointerOrientation = 'vertical';
        }


        // Text to use as aria-label for each column
        let valueLabelText = '', srText = '{label}, ';
        if (this.metadata?.enableDataDisplaySettings) {
            if (this.metadata?.labelDisplayOptions) {
                const doCount: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.count);
                const doPercent: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.percent) && !this.options.noPercentage;

                let symbol = '';
                if (this.options?.symbol) {
                    symbol = this.options.symbol;
                }
                if (doCount) {
                    valueLabelText += `[font-style:italic]{value${seriesIndex}.formatNumber(\'#.#a\')}${symbol}[/]`;
                    srText += `{value${seriesIndex}.formatNumber(\'#.#a\')}${symbol}`;
                }
                if (doCount && doPercent) {
                    valueLabelText += ', ';
                    srText += ', ';
                }
                if (doPercent) {
                    const percentIdentifier = `percent${seriesIndex}`;
                    valueLabelText += `[bold]{${percentIdentifier}.formatNumber(\'#.0\')}%[/]`;
                    srText += `{${percentIdentifier}.formatNumber(\'#.0\')}%`;
                }

            // this is necessary to reset the value in some situations
            } else {
                valueLabelText = '';
                srText = '';
            }
        } else {
            if (chartSeries.key === ChartMetricsEnum.ave) {
                const currency = this.options.styles.currency;
                if (currency) {
                    const currencySymbol = currencySymbolMap.get(currency);
                    if (currencySymbol) {
                        valueLabelText = `[font-style:italic]{value${seriesIndex}.formatNumber('${currencySymbol}#.#a')}[/]`;
                        srText += `{value${seriesIndex}.formatNumber('${currencySymbol}#.#a')}`;
                    } else {
                        valueLabelText = `[font-style:italic]{value${seriesIndex}.formatNumber('#.#a ${currency}')}[/]`;
                        srText += `{value${seriesIndex}.formatNumber('#.#a ${currency}')}`;
                    }
                }
            } else {
                srText += `{value${seriesIndex}.formatNumber(\'#.#a\')}`;
            }
        }
        series.itemReaderText = srText;
        if (this.valueLabel && !this.options.hideValueLabel) {
            this.valueLabel.label.text = valueLabelText;
        }
    }

    setUpBarSeries(series: ColumnSeries, seriesIndex: number, chartSeries: ChartSeries): void {
        series.dataFields.categoryY = 'label';
        series.dataFields.valueX = 'value' + seriesIndex;
        if (this.options.stacked && this.options.stackType === StackTypesEnum.percent) {
            series.dataFields.valueXShow = 'totalPercent';
        }
        if (this.options.stacked || !this.options.showFewerItemsAtLimitedSpace) {
            series.cursorTooltipEnabled = false;
        }

        const columnTemplate = series.columns.template;
        const bullet = series.bullets.push(new LabelBullet());
        bullet.label.text = '';
        bullet.interactionsEnabled = false;
        bullet.label.truncate = false;
        bullet.events.on('ready', (event) => {
            (event.target as LabelBullet).tabindex = -1;
        });
        if (this.options.stacked) {
            columnTemplate.height = am4core.percent(this.amchart.data?.length > 8 ? 75 : 100);
            if (chartSeries.labelColor) {
                bullet.label.fill = am4core.color(chartSeries.labelColor);
            } else {
                const barColor = new ColorModel(
                    this.styles.colorMap[chartSeries.key]
                    || this.styles.colorMap[chartSeries.colorMapKey],
                    null,
                    null
                );
                const relativeLuminance = barColor.getRelativeLuminance();
                bullet.label.fill = am4core.color(relativeLuminance < 0.5 ? '#fff' : '#000');
            }
            bullet.label.fontSize = '0.625rem';
            bullet.locationX = 0.5;
            bullet.label.hideOversized = true;
        } else {
            columnTemplate.column.cornerRadius(2, 2, 2, 2);
            columnTemplate.paddingBottom = 4;
            columnTemplate.height = am4core.percent(this.chartSeries.length > 1 ? 100 : 60);
            bullet.label.fill = am4core.color(chartColors.agprBlack);
            bullet.label.fontSize = '0.75rem';
            bullet.label.horizontalCenter = 'left';
            if (this.chartSeries.length > 1) {
                bullet.locationX = 1;
                bullet.dx = 12;
            } else {
                bullet.dx = 5;
            }
            bullet.label.hideOversized = false;
        }
        this.valueLabel = bullet;
        this.valueLabels.push({ labelBullet: bullet, chartSeries: chartSeries });
        columnTemplate.events.on('sizechanged', (event) => {
            this.adjustBarSeriesBulletVisibility(event.target, bullet);
        });
        if (this.options.trendIndicators && !this.options.stacked) {
            const uptrendColor = this.styles.colorMap?.['uptrendColor'];
            const downtrendColor = this.styles.colorMap?.['downtrendColor'];
            // Set series labels with arrows and color
            this.valueLabel.label.adapter.add('text', (a, b, c) => {
                const dataContext: DataPointModel = b.dataItem.dataContext as DataPointModel;
                const difference: number = +dataContext['value1'] - +dataContext['value0'];
                const percentDifference: string = (Math.abs(difference / +dataContext['value0']) * 100).toFixed(2);
                let text = '';
                if (difference > 0 && percentDifference !== '' + Infinity) {
                    text =  `[font-size:0.875rem ${uptrendColor ? uptrendColor : chartColors.agprPositive}]↑ ${percentDifference}%[/]`;
                } else if (difference < 0 && percentDifference !== '' + Infinity) {
                    text =  `[font-size:0.875rem ${downtrendColor ? downtrendColor : chartColors.agprNegative}]↓ ${percentDifference}%[/]`;
                } else {
                    text = '';
                }

                return text;
            });
        }
    }

    adjustBarSeriesBulletVisibility(column: Column, bullet: LabelBullet): void {
        if (this.isExporting) {
            return;
        }
        const columnHeightThreshold = this.options.styles.columnHeightThreshold || 16;
        if (column.pixelHeight < columnHeightThreshold) {
            bullet.hide();
            this.calculateBarSeriesLabelWidth(false);
        } else {
            bullet.show();
            this.calculateBarSeriesLabelWidth(true);
        }
    }

    setUpColumnSeries(series: ColumnSeries, seriesIndex: number, chartSeries: ChartSeries): void {
        series.dataFields.categoryX = 'label';
        series.dataFields.valueY = 'value' + seriesIndex;
        if (this.options.stacked && this.options.stackType === StackTypesEnum.percent) {
            series.dataFields.valueYShow = 'totalPercent';
        }
        series.cursorTooltipEnabled = false;

        const columnTemplate = series.columns.template;
        columnTemplate.width = am4core.percent(100);

        if (this.options.stacked) {
            const bullet = series.bullets.push(new LabelBullet());
            bullet.label.text = '';
            bullet.interactionsEnabled = false;
            bullet.label.truncate = false;
            bullet.label.hideOversized = true;
            bullet.events.on('ready', (event) => {
                (event.target as LabelBullet).tabindex = -1;
            });
            if (chartSeries.labelColor) {
                bullet.label.fill = am4core.color(chartSeries.labelColor);
            } else {
                const columnColor = new ColorModel(
                    this.styles.colorMap[chartSeries.key]
                    || this.styles.colorMap[chartSeries.colorMapKey],
                    null,
                    null
                );
                const relativeLuminance = columnColor.getRelativeLuminance();
                bullet.label.fill = am4core.color(relativeLuminance < 0.5 ? '#fff' : '#000');

            }
            bullet.label.fontSize = '0.625rem';
            bullet.locationY = 0.5;
            this.valueLabel = bullet;
            this.valueLabels.push({ labelBullet: bullet, chartSeries: chartSeries });
        } else {
            columnTemplate.column.cornerRadius(2, 2, 2, 2);
        }


        if (this.options.trendIndicators) {
            if (this.options.chartSpecificType === ChartSpecificTypesEnum.COLUMN) {
                this.valueAxis.extraMax = 0.3;
                this.amchart.series.values.forEach((_series,index) => {
                    const trendIndicator = _series.bullets.push(new am4charts.LabelBullet());
                    trendIndicator.label.adapter.add(
                        'text', (a, b, c) => {
                            const dataContext: DataPointModel = b.dataItem.dataContext as DataPointModel;
                            let difference: number;
                            let text = '';
                            // value 0 - current period, value 1 - previous period
                            if ((index !== 0 && dataContext['value1'] > dataContext['value0'])
                                                    || (index === 0 && dataContext['value1'] < dataContext['value0'])) {
                                difference = (+dataContext['value1'] - +dataContext['value0']);
                            }
                            const percentDifference: string = (Math.abs(difference / +dataContext['value0']) * 100).toFixed(2);
                            if (difference > 0 && percentDifference !== '' + Infinity) {
                                text =  `[font-size:0.875rem ${chartColors.agprPositive}]↑ ${percentDifference}%[/]`;
                            } else if (difference < 0 && percentDifference !== '' + Infinity) {
                                text =  `[font-size:0.875rem ${chartColors.agprNegative}]↓ ${percentDifference}%[/]`;
                            } else {
                                text = '';
                            }
                            return text;
                        }
                    );
                    trendIndicator.label.verticalCenter = 'bottom';
                    trendIndicator.label.paddingBottom = 2;
                    trendIndicator.label.truncate = false;
                });
            }
        }
    }

    setUpLegend(): void {
        if (this.options.hideLegend) {
            return;
        }
        if (this.options.legendContainerElement) {
            this.initCustomLegend();
        } else {
            // Set up the legend
            this.amchart.legend = new am4charts.Legend();
            this.amchart.legend.scrollable = true;
            if (this.isBarChart()) {
                this.amchart.legend.position = 'bottom';
                if (this.header && this.metadata && this.metadataContainer) {
                    this.metadataContainer.marginBottom = 5;
                }
            } else {
                this.amchart.legend.position = 'top';
                this.amchart.legend.marginBottom = 15;
            }
            this.amchart.legend.align = 'center';
            this.amchart.legend.contentAlign = 'left';

            if (this.options.styles['legendPosition']) {
                this.amchart.legend.position = this.options.styles['legendPosition'];
            }
            if (this.options.styles['legendContentAlign']) {
                this.amchart.legend.contentAlign = this.options.styles['legendContentAlign'];
            }

            this.amchart.legend.fontSize = '0.75rem';

            this.amchart.legend.labels.template.text = '{name}';
            this.amchart.legend.labels.template.truncate = true;
            this.amchart.legend.markers.template.scale = 0.75;

            this.amchart.legend.itemContainers.template.width = 70;
            const legendItemWidth = this.options.styles['legendItemWidth'];
            if (legendItemWidth) {
                this.amchart.legend.itemContainers.template.width = legendItemWidth;
            }
            this.amchart.legend.itemContainers.template.padding(2, 0, 2, 0);
            if (this.options.styles['legendItemPaddingLeft']) {
                this.amchart.legend.itemContainers.template.paddingLeft = this.options.styles['legendItemPaddingLeft'];
            }
            this.amchart.legend.itemContainers.template.focusable = false;
        }
    }

    getCustomLegendItems(): CustomLegendItem[] {
        const items: CustomLegendItem[] = [];
        if (this.isContentEmpty()) {
            this.legendWrapper.style.display = 'none';
            return items;
        } else {
            this.legendWrapper.style.display = 'flex';
        }

        this.chartSeries.forEach((chartSeries, index) => {
            const value = this.amchart.data.reduce((x, y) => x + (y['value' + index] || 0), 0);
            if (value) {
                items.push({
                    label: chartSeries.label,
                    color: this.styles.colorMap[chartSeries.colorMapKey] || this.styles.colorMap[chartSeries.key]
                });
            }
        });

        return items;
    }

    findChartBlockContainer(): void {
        let element = this.element;
        while (
            element.parentElement
            && !(
                element.classList.contains('agpr-block-content')
                || element.classList.contains('agpr-chartBlock-dashboard')
                || element.classList.contains('agpr-slide-chart')
                || element.classList.contains('agpr-chartDrillDownDialog-chartArea')
            )
        ) {
            element = element.parentElement;
        }
        this.chartBlockContainer = element;
    }

    setUpImageAsCategory(): void {
        if (this.options.imageAsCategory) {
            this.categoryAxis.renderer.line.strokeOpacity = 1;
            this.categoryAxis.renderer.line.strokeWidth = 2;
            this.categoryAxis.renderer.line.stroke = am4core.color(chartColors.agprGreyBorder);
            this.categoryAxis.renderer.labels.template.disabled = true;
            this.categoryAxis.dx = 1;

            const image = new am4core.Image();
            image.width = 20;
            image.height = 20;
            image.verticalCenter = 'middle';
            image.horizontalCenter = 'middle';
            image.tooltip = new am4core.Tooltip();
            image.tooltip.getFillFromObject = false;
            image.tooltip.pointerOrientation = 'left';
            image.tooltip.background.fill = am4core.color('#ffffff');
            image.tooltip.background.stroke = am4core.color('#ced4da');
            image.tooltip.background.cornerRadius = 8;
            image.tooltip.background.fillOpacity = 1;
            image.tooltip.background.filters.clear();
            image.tooltip.label.fill = am4core.color(chartColors.agprBlack);
            image.tooltip.pointerOrientation = 'left';
            image.tooltip.label.interactionsEnabled = true;
            image.tooltip.keepTargetHover = true;

            image.adapter.add('href', (href, target) => {
                const category = (target.dataItem as am4charts.CategoryAxisDataItem)?.category;

                if (category) {
                    let text;
                    if (this.options.categoryLabelMap) {
                        text = this.options.categoryLabelMap.get(category);
                    }
                    target.tooltipHTML = `
                        <div style="font-size: 0.875rem; color: #292929; line-height: 1.5;">
                            ${text ? text : category}
                        </div>
                    `;
                    return this.options.appBaseHref + 'assets/images/' + category.toLowerCase() + '_logo.png';
                }

                return href;
            });

            if (
                this.options.chartSpecificType === ChartSpecificTypesEnum.BAR
                || this.options.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR
            ) {
                this.categoryAxis.paddingLeft = 30;
                image.dx = -20;
            } else {
                this.categoryAxis.paddingBottom = 30;
                image.dx = -2;
                image.dy = 15;
            }

            this.categoryAxis.dataItems.template.bullet = image;

            if (this.options.responsive && this.responsiveCategoryAxis) {
                this.responsiveCategoryAxis.renderer.labels.template.disabled = true;
            }
        }
    }

    setUpResponsiveness(): void {
        if (this.options.responsive) {
            this.setUpSharedReportResponsiveness();
        } else {
            this.setUpMainAppResponsiveness();
        }
    }

    setUpSharedReportResponsiveness(): void {
        if (this.isBarChart()) {
            // Create another category axis and use amcharts default style
            // so the labels will appear beside the bars instead of trying to style the current category axis that we have
            const categoryAxisDirection = this.amchart[chartSpecificTypesToBaseAxisDirectionsMap.get(this.options.chartSpecificType)];
            this.responsiveCategoryAxis = categoryAxisDirection.push(new CategoryAxis());
            this.responsiveCategoryAxis.dataFields.category = 'label';
            this.responsiveCategoryAxis.cursorTooltipEnabled = false;
            this.responsiveCategoryAxis.renderer.grid.template.disabled = true;
            const responsiveCategoryLabel = this.responsiveCategoryAxis.renderer.labels.template;
            responsiveCategoryLabel.truncate = true;
            responsiveCategoryLabel.horizontalCenter = 'middle';
            responsiveCategoryLabel.verticalCenter = 'middle';
            responsiveCategoryLabel.textAlign = 'middle';
            responsiveCategoryLabel.truncate = true;
            responsiveCategoryLabel.fullWords = false;
            this.responsiveCategoryAxis.renderer.labels.template.maxWidth = 400;
            responsiveCategoryLabel.dx = -5;
            this.valueAxis.renderer.labels.template.dx = -30;

            if (this.options.sharedReportChartMinHeight) {
                this.amchart.svgContainer.htmlElement.style.minHeight = this.options.sharedReportChartMinHeight + 'px';
            }
            this.amchart.events.on('sizechanged', (event) => {
                const chart = event.target;
                if (chart.pixelWidth > 700) {
                    this.responsiveCategoryAxis.renderer.disabled = false;
                    this.categoryAxis.renderer.disabled = true;
                } else {
                    this.responsiveCategoryAxis.renderer.disabled = true;
                    this.categoryAxis.renderer.disabled = false;
                    this.categoryLabel.dy = -25;
                }
            });
        } else {
            this.valueAxis.dx = -50;
            this.valueAxis.renderer.line.disabled = true;
            this.amchart.paddingLeft = 70;
        }

        // Add tooltip
        this.amchart.series.each((series, index) => {
            const columnTemplate = (series as ColumnSeries).columns.template;
            const categoryIdentifier = this.chartSeries.length === 1 ? 'label' : 'name';
            const seriesIndex = +series.dataFields['percent']?.match(/\d+/)[0];
            if (this.options.noPercentage){
                columnTemplate.tooltipText = `[font-size:0.75rem]{${categoryIdentifier}}: [/][bold font-size:0.75rem]{value${seriesIndex}}[/]`;
            } else {
                columnTemplate.tooltipText = `[font-size:0.75rem]{${categoryIdentifier}}: [/][bold font-size:0.75rem]{value${seriesIndex}} ({percent}%)[/]`;
            }
            if (!columnTemplate.tooltip) {
                columnTemplate.tooltip = new am4core.Tooltip();
            }
            columnTemplate.tooltip.getFillFromObject = false;
            columnTemplate.tooltip.background.fill = am4core.color(chartColors.agprWhite);
            columnTemplate.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        });

        this.amchart.responsive.enabled = true;
        this.amchart.responsive.useDefault = false;

        // RULES

        // Laptop and desktop screen
        this.amchart.responsive.rules.push(
            {
                id: 'large',
                relevant: (target) => target.pixelWidth > 768,
                state: function (target, stateId) {
                    if (target instanceof am4charts.ValueAxis) {
                        const valueAxisState = target.states.create(stateId);
                        valueAxisState.properties.width = am4core.percent(100);
                        return valueAxisState;
                    }
                    if (target instanceof am4charts.AxisLabel) {
                        const labelState = target.states.create(stateId);
                        labelState.properties.fontSize = 16;
                        return labelState;
                    }
                }

            }
        );

        // Tablet screen
        this.amchart.responsive.rules.push(
            {
                id: 'medium',
                relevant: (target) => target.pixelWidth <= 700 && target.pixelWidth > 500,
                state: function (target, stateId) {
                    if (target instanceof am4charts.ValueAxis) {
                        const valueAxisState = target.states.create(stateId);
                        valueAxisState.properties.width = am4core.percent(90);
                        return valueAxisState;
                    }
                    if (target instanceof am4charts.AxisLabel) {
                        const labelState = target.states.create(stateId);
                        labelState.properties.fontSize = 14;
                        return labelState;
                    }
                }
            }
        );

        // Mobile screen
        this.amchart.responsive.rules.push(
            {
                id: 'small',
                relevant: (target) => target.pixelWidth <= 500,
                state: function (target, stateId) {
                    if (target instanceof am4charts.ValueAxis) {
                        const valueAxisState = target.states.create(stateId);
                        valueAxisState.properties.width = am4core.percent(80);
                        return valueAxisState;
                    }
                    if (target instanceof am4charts.AxisLabel) {
                        const labelState = target.states.create(stateId);
                        labelState.properties.fontSize = 14;
                        return labelState;
                    }
                }
            }
        );

        if (this.amchart.data.length > 0 && this.options.columnHeight) {
            // This sets column height to a fixed size and auto-adjusts chart container height
            // so the bar won't be too big or too small when there're a few bars or many bars.
            const cellSize = 50;
            this.amchart.events.on('datavalidated', (event) => {
                const paddingTop = this.options.styles?.customPadding?.top || (this.options.noPadding ? 0 : 15);
                this.amchart.svgContainer.htmlElement.style.height = paddingTop + this.amchart.data.length * cellSize + 'px';
            });
        }
    }

    setUpMainAppResponsiveness(): void {
        this.setUpResponsivenessForLegend(this.amchart);
        this.amchart.events.on('sizechanged', ev => {
            this.setUpResponsivenessForLegend(ev.target);
            this.amchart.data = this.modifyDataArray();
            this.adjustValueAxisExtraMax();

        });
        this.categoryAxis.events.on('sizechanged', (event) => {
            this.updateCategoryLabelRotation(this.categoryLabel);
        });

        if (this.options.showFewerItemsAtLimitedSpace && this.chartBlockContainer) {
            this._blockContainerObserver?.disconnect();
            this._blockContainerObserver = new ResizeObserver(() => {
                if (this.amchart) {
                    this.amchart.data = this.modifyDataArray();
                    this.adjustValueAxisExtraMax();
                }
            });
            this._blockContainerObserver.observe(this.chartBlockContainer);
        }
    }

    setUpResponsivenessForLegend(chart: XYChart): void {
        const smallChartWidth = ChartSizeHelper.getMinWidthAtMinCanvas();
        const smallChartHeight = ChartSizeHelper.getHeight(3, 2);
        const legendItemWidth = this.options.styles['legendItemWidth'];
        if (this.amchart.legend) {
            this.amchart.legend.itemContainers.template.width = chart.pixelWidth <= smallChartWidth ? 100 : 130;
            if (legendItemWidth) {
                this.amchart.legend.itemContainers.template.width = legendItemWidth;
            }
            if (!this.options.legendScrollDisabled) {
                this.amchart.legend.maxHeight = chart.pixelHeight <= smallChartHeight ?
                    Math.max(chart.pixelHeight * 0.2, 30) : chart.pixelHeight * 0.25;
            }
        }

        this.amchart?.legend?.afterDraw();
    }

    adjustValueAxisExtraMax(): void {
        if (this.options.includeTotalSeries && this.isBarChart() && this.valueAxis) {
            this.valueAxis.extraMax = 0.3;
            if (this.valueAxis.pixelWidth > 0) {
                let totalSeriesLabelWidth = 10;
                if (this.metadata?.labelDisplayOptions) {
                    const doCount: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.count);
                    const doPercent: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.percent) && !this.options.noPercentage;
                    if (doCount) {
                        totalSeriesLabelWidth += 50;
                    }
                    if (doPercent) {
                        totalSeriesLabelWidth += 40;
                    }
                }
                this.valueAxis.extraMax = totalSeriesLabelWidth / (this.valueAxis.pixelWidth - totalSeriesLabelWidth);
            }
        }
    }

    /**
     * Updates the current presentation of data in the chart
     */
    public updateData(data: DataPointModel[][]): void {
        this.rawData = data;

        if (!this.amchart) {
            return;
        }
        if (!data) {
            this.amchart.data = null;
            this.ready.next(true);
            return;
        }

        const d = this.modifyDataArray();
        this.amchart.data = d;
        if (!data?.includes(null) && this.isContentEmpty()) {
            this.showNoContentMessage();
            this.noContentLabel.dom.style.display = 'block';
        } else {
            if (this.noContentLabel) {
                this.noContentLabel.dom.style.display = 'none';
            }
        }

        this.amchart.series.clear();

        this.setUpSeries(d);
        this.setUpResponsiveness();
        this.setUpImageAsCategory();

        this.updateCategoryLabelRotation(this.categoryLabel);
        this.updateSeriesStyles();
        this.ready.next(true);
    }

    setUpSeries(data: DataPointModel[]): void {
        if (this.options.reverseBarSeriesOrder && this.options.chartSpecificType === ChartSpecificTypesEnum.BAR) {
            for (let i = this.chartSeries.length - 1; i >= 0; i--) {
                this.addSeriesFromData(data, i, this.chartSeries[i]);
            }
            if (this.amchart.legend) {
                this.amchart.legend.reverseOrder = true;
            }
        } else {
            for (let i = 0; i < this.chartSeries.length; i++) {
                this.addSeriesFromData(data, i, this.chartSeries[i]);
            }
            if (this.amchart.legend) {
                this.amchart.legend.reverseOrder = false;
            }
        }
        // Create series for total to display the total to the right of columns
        if (this.options.includeTotalSeries && this.isBarChart()) {
            this.adjustValueAxisExtraMax();
            this.totalSeries = new ColumnSeries();
            this.totalSeries.dataFields.valueX = 'valueZero';
            this.totalSeries.dataFields.categoryY = 'label';
            this.totalSeries.stacked = true;
            this.totalSeries.hiddenInLegend = true;
            this.totalSeries.columns.template.strokeOpacity = 0;

            const totalBullet = this.totalSeries.bullets.push(new LabelBullet());
            totalBullet.label.hideOversized = false;
            totalBullet.label.fontSize = '0.75rem';
            totalBullet.label.truncate = false;
            let labelText = '';
            if (this.metadata?.labelDisplayOptions) {
                const doCount: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.count);
                const doPercent: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.percent) && !this.options.noPercentage;
                if (doCount) {
                    labelText += '[font-style:italic]{valueTotal}[/]';
                }
                if (doCount && doPercent) {
                    labelText += ', ';
                }
                if (doPercent) {
                    labelText += '[bold]{percentTotal.formatNumber(\'#.0\')}%';
                }
            }
            totalBullet.label.text = labelText;
            totalBullet.label.dx = 10;
            totalBullet.label.horizontalCenter = 'left';

            if (!this.amchart.series.contains(this.totalSeries)) {
                this.amchart.series.push(this.totalSeries);
            }
        }
    }

    addSeriesFromData(data: DataPointModel[], index: number, series: ChartSeries): void {
        const value = data.reduce((x, y) => x + (y['value' + index] as number || 0), 0);
        if (value) {
            this.addSeries(index, series);
        }
    }

    modifyDataArray(): DataPointModel[] {
        if (this.rawData[0]?.length) {
            let d = JSON.parse(JSON.stringify(this.rawData[0]));
            const smallChartHeight = ChartSizeHelper.getCellHeight();
            if (this.options.barDisplayLimit) {
                this.chartDisplayLimit = this.options.barDisplayLimit;
            }
            if (this.isBarChart()) {
                if (this.options.showFewerItemsAtLimitedSpace) {
                    const chartContainerHeight = parseInt(getComputedStyle(this.chartBlockContainer)['height'], 10);
                    if (chartContainerHeight < smallChartHeight && !this.isForSharedReport) {
                        // show fewer items if the chart is short
                        d = d.slice(-(this.options.smallChartDisplayLimit || this.smallChartDisplayLimit));
                    } else {
                        d = d.slice(-this.chartDisplayLimit);
                    }
                    if (this.amchart) {
                        this.amchart.svgContainer.htmlElement.style.height = chartContainerHeight + 'px';
                    }
                } else {
                    d = d.slice(-this.chartDisplayLimit);
                }
                if (d.length < 10 && d.length > 0 && this.options.columnHeight) {
                    // This sets column height to a fixed size and auto-adjusts chart container height
                    // so the bar won't be too big when there're a few bars
                    const cellSize = this.options.columnHeight;
                    const paddingTop = this.options.styles?.customPadding?.top || (this.options.noPadding ? 0 : 15);
                    const headerHeight = this.header?.contentHeight || 55;
                    const paddingBottom = this.amchart.paddingBottom || 15;
                    if (this.amchart) {
                        this.amchart.svgContainer.htmlElement.style.height =
                            paddingTop + headerHeight + d.length * cellSize + paddingBottom + 'px';
                    }
                }
                if (d.length <= 1) {
                    this.categoryAxis.renderer.cellStartLocation = 0.15;
                    this.categoryAxis.renderer.cellEndLocation = 0.85;
                } else {
                    if (this.options.categoryLabelStackedOnTop) {
                        this.categoryAxis.renderer.cellStartLocation = 0.25;
                        this.categoryAxis.renderer.cellEndLocation = 0.85;
                    } else {
                        this.categoryAxis.renderer.cellStartLocation = 0.2;
                        this.categoryAxis.renderer.cellEndLocation = 0.8;
                    }
                }
            } else {
                d = d.slice(0, this.chartDisplayLimit);
            }

            return d;
        } else {
            return [];
        }
    }

    updateSeriesStyles(): void {
    }

    /**
     * Exports a chart via amcharts
     */
    public async export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {
        const dataFields = {};
        if (this.options.exportLabels) {
            dataFields['label'] = this.options.exportLabels.label;
            // these if statements need to separate out due to the order of exported fields
            if (!this.options.includeTotalSeries || this.chartSeries.length > 1) {
                this.chartSeries.forEach((chartSeries, index) => {
                    if (!this.amchart.series.values[index]?.hiddenInLegend) {
                        dataFields['value' + index] = this.options.exportLabels[chartSeries.key]?.countLabel;
                    }
                });
            }
            if (this.options.includeTotalSeries) {
                dataFields['valueTotal'] = this.options.exportLabels[this.options.exportLabelTotalKey].countLabel;
            }
            if (!this.options.includeTotalSeries || this.chartSeries.length > 1) {
                this.chartSeries.forEach((chartSeries, index) => {
                    if (!this.amchart.series.values[index]?.hiddenInLegend) {
                        dataFields['percent' + index] = this.options.exportLabels[chartSeries.key]?.percentLabel;
                    }
                });
            }
            if (this.options.includeTotalSeries) {
                dataFields['percentTotal'] = this.options.exportLabels[this.options.exportLabelTotalKey].percentLabel;
            }
        } else {
            dataFields['label'] = this.options.label;
            if (this.options.styles.chartHeaderSettings?.previousPeriod) {
                dataFields['value1'] = this.options.xValueLabel;
                dataFields['percent1'] = this.options.percentLabel;
                dataFields['value0'] = this.options.previousPeriodCountExportLabel;
                dataFields['percent0'] = this.options.previousPeriodPercentExportLanbel;
            } else {
                dataFields['value0'] = this.options.xValueLabel;
                dataFields['percent0'] = this.options.percentLabel;
            }
        };

        this.amchart.exporting.dataFields = dataFields;
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        if (type === 'csv' || type === 'json') {
            let export_;
            requestAnimationFrame(async () => {
                const temp: any[] = this.amchart.data;
                const ogChartConfig = reportsBlockMappingConfigurationMap.get(this.metadata.type as ReportBlockTypeEnum);
                const rawData = JSON.parse(JSON.stringify(this.rawData[0]));
                this.amchart.data = rawData;
                if (typeof ogChartConfig.reverseDataPointOrder === 'function') {
                    if (ogChartConfig.reverseDataPointOrder(this.options.chartSpecificType)) {
                        this.amchart.data.reverse();
                    }
                } else {
                    if (ogChartConfig.reverseDataPointOrder) {
                        this.amchart.data.reverse();
                    }
                }
                export_ = await this.amchart.exporting.export(type);
                this.amchart.data = temp;
            });

            return export_;
        }

        return await this.amchart.exporting.export(type);
    }

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        this.isExporting = true;
        super.setExportStyles(scale, keepFontsize);

        // Now that the height isn't dynamic, we need to scale the image so that it doesn't get stretched
        if (scale) {
            this.amchart.htmlContainer.style.width = (800 * scale) + 'px';
        }

        if (this.isBarChart() && this.amchart.data?.length < 10 && this.amchart.data?.length > 0 && this.options.columnHeight) {
            // This sets column height to a fixed size and auto-adjusts chart container height
            // so the bar won't be too big when there're a few bars
            const cellSize = 65;
            const paddingTop = this.options.styles?.customPadding?.top || (this.options.noPadding ? 0 : 15);
            const headerHeight = this.header?.contentHeight || 55;
            this.amchart.htmlContainer.style.height =
                paddingTop + headerHeight + this.amchart.data.length * cellSize + 'px';
        }

        if (this.metadataContainer) {
            this.metadataContainer.layout = 'horizontal';
            if (this.primarySeriesLabel) {
                this.primarySeriesLabel.textAlign = 'end';
                this.primarySeriesLabel.valign = 'top';
            }
        }

        this.categoryLabel.fontSize = 14;
        if (this.amchart.legend) {
            this.amchart.legend.fontSize = 14;
            this.amchart.legend.markers.template.scale = 1;
            this.amchart.legend.itemContainers.template.width = 150;
        }

        if (this.amchart.scrollbarX) {
            this.amchart.scrollbarX.visible = false;
        }
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        this.isExporting = false;
        if (!this.amchart) {
            return;
        }
        this.amchart.seriesContainer.paddingTop = 0;

        if (this.metadataContainer) {
            this.metadataContainer.layout = 'vertical';
            if (this.primarySeriesLabel) {
                this.primarySeriesLabel.textAlign = 'start';
            }
        }

        this.categoryLabel.fontSize = '0.625rem';
        this.categoryLabel.width = undefined;
        if (this.amchart.legend) {
            this.amchart.legend.fontSize = '0.688rem';
            this.amchart.legend.markers.template.scale = 0.75;
            this.amchart.legend.itemContainers.template.width = 100;
        }

        super.unsetExportStyles();

        if (this.isBarChart() && this.amchart.data?.length < 10 && this.amchart.data?.length > 0 && this.options.columnHeight) {
            // This sets column height to a fixed size and auto-adjusts chart container height
            // so the bar won't be too big when there're a few bars
            const cellSize = this.options.columnHeight;
            const paddingTop = this.options.styles?.customPadding?.top || (this.options.noPadding ? 0 : 15);
            const headerHeight = this.header?.contentHeight || 55;
            this.amchart.htmlContainer.style.height =
                paddingTop + headerHeight + this.amchart.data.length * cellSize + 'px';
        }

        if (this.amchart.scrollbarX) {
            this.amchart.scrollbarX.visible = true;
        }
        if (this.metadataContainer) {
            this.metadataContainer.layout = 'horizontal';
        }
    }

    public isContentEmpty(): boolean {
        return this.amchart?.data && this.amchart.data?.length === 0;
    }

    public showNoContentMessage(message?: string): void {
        super.showNoContentMessage();
        this.categoryAxis.renderer.baseGrid.disabled = true;
        this.categoryAxis.renderer.line.disabled = true;
        this.valueAxis.renderer.baseGrid.disabled = true;
        this.valueAxis.renderer.line.disabled = true;
    }

    public clone(deepCloneSeries: boolean = true): CategoryValueXYChart {
        return new CategoryValueXYChart(
            deepCloneSeries ? this.series.map(s => s.clone()) : this.series,
            this.options,
            this.metadata,
            this.chartSeries
        );
    }

    public isBarChart(): boolean {
        return this.options.chartSpecificType === ChartSpecificTypesEnum.BAR ||
            this.options.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR;
    }

    public isColumnChart(): boolean {
        return this.options.chartSpecificType === ChartSpecificTypesEnum.COLUMN ||
            this.options.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_COLUMN;
    }

    public destroy(): void {
        this._blockContainerObserver?.disconnect();
        super.destroy();
    }
}
